// =============================================================================
// src/features/copilot-chat/copilot-dom.ts
// Helpers de DOM/markup e refs de elementos do chat do Copiloto IA.
//
// Proveniencia: porte FIEL de assets/copilot-chat.js — `h()` (43-47), `orbSvg()`
//   (48-50), `announce()`/`scrollToEnd()` (51-55), o timing `D()` (31), o mapa
//   de icones `TI` (33-41), `reduceMotion` (19) e a coleta de refs (15-27).
//
// Nenhuma lib externa (so DOM/SVG nativo) — por isso NAO ha consulta Context7
//   aqui (a regra cobre libs de terceiros; `createElement`/`querySelector` sao
//   APIs estaveis do navegador). SRP: so DOM/markup, sem dados, sem regra de chat.
// =============================================================================

import { qs } from '../../app/dom';

/** True se o usuario pediu menos animacao (porte de `reduceMotion`, 19). */
export const copilotReduceMotion: boolean = window.matchMedia(
  '(prefers-reduced-motion: reduce)',
).matches;

/**
 * Encurta uma duracao sob prefers-reduced-motion (porte de `D(ms)`, 31).
 * Sem reduce, devolve o valor original; com reduce, no maximo 40ms (ms/8).
 */
export function scaleDuration(ms: number): number {
  return copilotReduceMotion ? Math.min(40, ms / 8) : ms;
}

/**
 * Mapa de icones Tabler inline (porte FIEL de `var TI`, 33-41). HTML literal,
 *   reaproveitado por cards/controles do plano/rascunho (DRY).
 */
export const COPILOT_TABLER_ICONS = {
  check: '<i class="ti ti-check" aria-hidden="true"></i>',
  refresh: '<i class="ti ti-refresh cop-ico sm" aria-hidden="true"></i>',
  pencil: '<i class="ti ti-pencil cop-ico sm" aria-hidden="true"></i>',
  x: '<i class="ti ti-x cop-ico sm" aria-hidden="true"></i>',
  copy: '<i class="ti ti-copy cop-ico sm" aria-hidden="true"></i>',
  spark: '<i class="ti ti-sparkles cop-ico sm" aria-hidden="true"></i>',
  file: '<i class="ti ti-file-text cop-ico sm" aria-hidden="true"></i>',
} as const;

/**
 * Converte uma string HTML no seu primeiro elemento (porte de `h()`, 43-47).
 * Usa <template> para parse seguro; lanca com o HTML ofensivo se o resultado
 *   nao for um elemento (CLAUDE.md regra 11 — todo call site passa markup valido).
 *
 * @example
 *   const card = parseElement('<section class="cop-card">…</section>');
 */
export function parseElement(html: string): HTMLElement {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const first = template.content.firstElementChild;
  if (!(first instanceof HTMLElement)) {
    throw new Error(
      `copilot-dom.ts: HTML sem elemento raiz, recebido ${JSON.stringify(html.slice(0, 80))}`,
    );
  }
  return first;
}

/** Markup do orb animado da IA com a classe de tamanho dada (porte de `orbSvg()`, 48-50). */
export function orbSvg(sizeClass: string): string {
  return (
    `<span class="ai-orb ${sizeClass}" aria-hidden="true">` +
    '<svg class="ai-orb__svg" viewBox="0 0 32 32"><use href="#aiOrbSym"/></svg></span>'
  );
}

/**
 * Refs dos elementos fixos do takeover, coletados uma vez no init (porte de 15-27).
 * Campos opcionais (null) replicam os guards `if (el)` do original.
 */
export interface CopilotChatRefs {
  readonly takeover: HTMLElement;
  readonly launcher: HTMLElement | null;
  readonly scrollEl: HTMLElement | null;
  readonly threadEl: HTMLElement | null;
  readonly heroEl: HTMLElement | null;
  readonly liveEl: HTMLElement | null;
  readonly inputEl: HTMLTextAreaElement | null;
  readonly sendEl: HTMLButtonElement | null;
  readonly formEl: HTMLFormElement | null;
}

/**
 * Coleta as refs do takeover. Devolve null se #copilotChatTakeover nao existir
 *   (porte do early `if (!takeover) return;`, 17) — o init aborta em silencio.
 */
export function collectCopilotChatRefs(): CopilotChatRefs | null {
  const takeover = document.getElementById('copilotChatTakeover');
  if (!takeover) return null;
  return {
    takeover,
    launcher: document.getElementById('copilotChatLauncher'),
    scrollEl: document.getElementById('copScroll'),
    threadEl: document.getElementById('copThread'),
    heroEl: document.getElementById('copHero'),
    liveEl: document.getElementById('copLive'),
    inputEl: qs<HTMLTextAreaElement>('#copInput'),
    sendEl: qs<HTMLButtonElement>('#copSend'),
    formEl: qs<HTMLFormElement>('#copForm'),
  };
}

/** Publica uma mensagem na regiao aria-live (porte de `announce()`, 51). */
export function announceToLiveRegion(liveEl: HTMLElement | null, message: string): void {
  if (liveEl) liveEl.textContent = message;
}

/**
 * Rola o container ate o fim, suave (ou instantaneo sob reduce) (porte de
 *   `scrollToEnd()`, 52-55). Aceita ref opcional para replicar o guard `if (!scrollEl)`
 *   do original.
 */
export function scrollThreadToEnd(scrollEl: HTMLElement | null): void {
  if (!scrollEl) return;
  scrollEl.scrollTo({
    top: scrollEl.scrollHeight,
    behavior: copilotReduceMotion ? 'auto' : 'smooth',
  });
}
